import { type FormEvent, useId, useState } from "react";
import toast from "react-hot-toast";

import { Icon } from "@shared/ui";

import type {
  PolicyLookupCredentialInput,
  SafePolicyLookupCredential,
} from "@platform/automatic-api";

import Eye from "../../icons/Eye";
import EyeOff from "../../icons/EyeOff";
import Info from "../../icons/Info";
import {
  createPolicyLookupCredential,
  updatePolicyLookupCredential,
} from "../../lib/policyLookupSettings";
import ButtonPrimary from "../ButtonPrimary";
import ButtonSecondary from "../ButtonSecondary";
import { type Address, AddressFields, emptyAddress } from "./AddressFields";
import FieldLabel from "./FieldLabel";

const inputClassName =
  "w-full rounded border border-gray-300 p-2.5 text-sm placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

const AXLE_EMAIL_ERROR = "Email must use the @axlehq.com domain.";

const isAxleEmail = (email: string): boolean =>
  /^[^@\s]+@axlehq\.com$/i.test(email.trim());

type CredentialFormState = {
  username: string;
  password: string;
  companyCode: string;
  businessName: string;
  firstName: string;
  lastName: string;
  address: Address;
  mfaEmail: string;
};

export const emptyCredentialForm = (): CredentialFormState => ({
  username: "",
  password: "",
  companyCode: "",
  businessName: "",
  firstName: "",
  lastName: "",
  address: emptyAddress,
  mfaEmail: "",
});

export const toCredentialFormState = (
  credential: SafePolicyLookupCredential
): CredentialFormState => ({
  username: credential.username ?? "",
  password: "",
  companyCode: credential.companyCode ?? "",
  businessName: credential.businessName ?? "",
  firstName: credential.firstName ?? "",
  lastName: credential.lastName ?? "",
  address: {
    ...emptyAddress,
    ...credential.address,
    addressLine2: credential.address?.addressLine2 ?? "",
  },
  mfaEmail: credential.mfaEmail ?? "",
});

// Identifies the field used as the carrier login.
type ResolvedIdentifierField = "username" | "companyCode" | "businessName";

// Farmers uses username/password without an MFA email.
const FARMERS_LIENHOLDER_PORTAL_INTEGRATION = "farmers-lienholder-portal";

// Company-code and business-name integrations duplicate their identifier into
// username because it is the database primary key.
const identifierValue = (
  form: CredentialFormState,
  identifierField: ResolvedIdentifierField
): string => {
  if (identifierField === "companyCode") return form.companyCode;
  if (identifierField === "businessName") return form.businessName;
  return form.username;
};

// Builds the API payload without untouched optional fields.
const buildCredentialInput = (
  form: CredentialFormState,
  identifierField: ResolvedIdentifierField
): PolicyLookupCredentialInput => ({
  username: identifierValue(form, identifierField),
  password: form.password || undefined,
  companyCode: form.companyCode || undefined,
  businessName: form.businessName || undefined,
  firstName: form.firstName || undefined,
  lastName: form.lastName || undefined,
  address: form.address.addressLine1
    ? { ...form.address, addressLine2: form.address.addressLine2 || null }
    : undefined,
  mfaEmail: form.mfaEmail || undefined,
});

const toSafeCredential = (
  integration: string,
  form: CredentialFormState,
  identifierField: ResolvedIdentifierField
): SafePolicyLookupCredential => ({
  integration,
  username: identifierValue(form, identifierField),
  status: "active",
  companyCode: form.companyCode || null,
  businessName: form.businessName || null,
  firstName: form.firstName || null,
  lastName: form.lastName || null,
  address: form.address.addressLine1
    ? { ...form.address, addressLine2: form.address.addressLine2 || null }
    : null,
  mfaEmail: form.mfaEmail || null,
});

/**
 * Mirrors the integration auth pattern. `auto` represents an integration
 * whose required credential fields have not been configured yet.
 */
export type IdentifierField = ResolvedIdentifierField | "auto";

type CredentialFormProps = {
  clientId: string;
  integration: string;
  identifierField: IdentifierField;
  initialForm: CredentialFormState;
  submitLabel: string;
  onCancel?: () => void;
  onSaved: (_credential: SafePolicyLookupCredential) => void;
  isUpdate: boolean;
};

export const CredentialForm = ({
  clientId,
  integration,
  identifierField,
  initialForm,
  submitLabel,
  onCancel,
  onSaved,
  isUpdate,
}: CredentialFormProps) => {
  const [form, setForm] = useState<CredentialFormState>(initialForm);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaEmailError, setMfaEmailError] = useState<string | null>(null);
  const idPrefix = useId();
  const usesUsername =
    identifierField === "username" || identifierField === "auto";
  const usesBusinessName = identifierField === "businessName";
  const requiresEmail =
    (usesUsername || usesBusinessName) &&
    integration !== FARMERS_LIENHOLDER_PORTAL_INTEGRATION;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (requiresEmail && !isAxleEmail(form.mfaEmail)) {
      setMfaEmailError(AXLE_EMAIL_ERROR);
      return;
    }

    setMfaEmailError(null);
    // "auto" carriers only ever collect a username (company code is
    // exclusive to the company-code pattern), so it always resolves there.
    const resolvedIdentifierField: ResolvedIdentifierField =
      identifierField === "auto" ? "username" : identifierField;

    setIsSaving(true);
    try {
      const action = isUpdate
        ? updatePolicyLookupCredential
        : createPolicyLookupCredential;
      const [, error] = await action(
        clientId,
        integration,
        "active",
        buildCredentialInput(form, resolvedIdentifierField)
      );
      if (error) {
        toast.error(error.message || "Could not save credential");
        return;
      }
      toast.success(isUpdate ? "Credential updated" : "Credential connected");
      onSaved(toSafeCredential(integration, form, resolvedIdentifierField));
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-y-4 border border-gray-200 bg-gray-50 rounded-md p-6"
    >
      {usesUsername && (
        <div className="flex flex-col gap-y-2">
          <FieldLabel htmlFor={`${idPrefix}-username`} required>
            Username
          </FieldLabel>
          <input
            id={`${idPrefix}-username`}
            className={inputClassName}
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            disabled={isUpdate}
            required
          />
        </div>
      )}
      {identifierField === "companyCode" && (
        <div className="flex flex-col gap-y-2">
          <FieldLabel htmlFor={`${idPrefix}-companyCode`} required>
            Company code
          </FieldLabel>
          <input
            id={`${idPrefix}-companyCode`}
            className={inputClassName}
            value={form.companyCode}
            onChange={(e) => setForm({ ...form, companyCode: e.target.value })}
            disabled={isUpdate}
            required
          />
          {isUpdate && (
            <div className="flex items-center gap-x-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
              <Icon
                icon={Info}
                height="h-4"
                width="w-4"
                className="fill-gray-400 shrink-0"
              />
              <span>
                Company codes are not changeable. Contact Axle to provide
                support.
              </span>
            </div>
          )}
        </div>
      )}
      {usesBusinessName && (
        <>
          <div className="flex flex-col gap-y-2">
            <FieldLabel htmlFor={`${idPrefix}-businessName`} required>
              Business name
            </FieldLabel>
            <input
              id={`${idPrefix}-businessName`}
              className={inputClassName}
              value={form.businessName}
              onChange={(e) =>
                setForm({ ...form, businessName: e.target.value })
              }
              disabled={isUpdate}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-y-2">
              <FieldLabel htmlFor={`${idPrefix}-firstName`} required>
                First name
              </FieldLabel>
              <input
                id={`${idPrefix}-firstName`}
                className={inputClassName}
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
                required
              />
            </div>
            <div className="flex flex-col gap-y-2">
              <FieldLabel htmlFor={`${idPrefix}-lastName`} required>
                Last name
              </FieldLabel>
              <input
                id={`${idPrefix}-lastName`}
                className={inputClassName}
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <FieldLabel required>Business address</FieldLabel>
            <AddressFields
              address={form.address}
              onChange={(address) => setForm({ ...form, address })}
            />
          </div>
        </>
      )}
      {usesUsername && (
        <div className="flex flex-col gap-y-2">
          <FieldLabel htmlFor={`${idPrefix}-password`} required>
            Password
          </FieldLabel>
          <div className="relative">
            <input
              id={`${idPrefix}-password`}
              type={showPassword ? "text" : "password"}
              className={`${inputClassName} pr-10`}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <Icon
                icon={showPassword ? EyeOff : Eye}
                height="h-4"
                width="w-4"
                className="fill-gray-400"
              />
            </button>
          </div>
        </div>
      )}
      {requiresEmail && (
        <div className="flex flex-col gap-y-2">
          <FieldLabel htmlFor={`${idPrefix}-mfaEmail`} required>
            Email
          </FieldLabel>
          <input
            id={`${idPrefix}-mfaEmail`}
            type="email"
            className={`${inputClassName} ${
              mfaEmailError ? "border-red-500" : ""
            }`}
            value={form.mfaEmail}
            onChange={(e) => {
              const mfaEmail = e.target.value;
              setForm({ ...form, mfaEmail });
              if (mfaEmailError && isAxleEmail(mfaEmail)) {
                setMfaEmailError(null);
              }
            }}
            onBlur={() => {
              if (form.mfaEmail && !isAxleEmail(form.mfaEmail)) {
                setMfaEmailError(AXLE_EMAIL_ERROR);
              }
            }}
            aria-invalid={mfaEmailError ? true : undefined}
            aria-describedby={
              mfaEmailError ? `${idPrefix}-mfaEmail-error` : undefined
            }
            required
          />
          {mfaEmailError && (
            <p
              id={`${idPrefix}-mfaEmail-error`}
              className="text-sm text-red-600"
              role="alert"
            >
              {mfaEmailError}
            </p>
          )}
        </div>
      )}
      <div className="flex gap-x-2">
        {/* Company code is the only field this pattern collects, and it's
            locked once set — there's nothing left here to save. */}
        {!(isUpdate && identifierField === "companyCode") && (
          <ButtonPrimary
            text={isSaving ? "Saving…" : submitLabel}
            type="submit"
            disabled={isSaving}
            icon={undefined}
            className=""
          />
        )}
        {onCancel && (
          <ButtonSecondary
            text="Cancel"
            type="button"
            onClick={onCancel}
            icon={undefined}
            className=""
          />
        )}
      </div>
    </form>
  );
};

export default CredentialForm;
